import * as React from 'react';

import type { ApolloError } from '@apollo/client';
import { useApolloClient } from '@apollo/client';

import aiCoachChatMessagesByChatSessionIdQuery from 'bundles/ai-course-coach/queries/aiCoachChatMessagesByChatSessionId.graphql';
import store from 'bundles/ai-course-coach/store';
import type { ResponseMessage, UpdateChatHistory, UserMessage } from 'bundles/ai-course-coach/store/actions/types';
import { setChatState, updateChatHistory } from 'bundles/ai-course-coach/store/dispatcher';
import type { GlobalState } from 'bundles/ai-course-coach/store/types';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';

import type {
  AiCoach_ChatMessagesPaginatedResultQuery as ChatMessagesPaginatedResultQuery,
  AiCoach_ChatMessagesPaginatedResultQueryVariables as ChatMessagesPaginatedResultQueryVariables,
} from '../queries/__generated__/aiCoachChatMessagesByChatSessionId';
import { MESSAGE_SENDER } from './constants';

type Props = {
  historyLimit: number;
  chatSessionId?: string;
  recommendedActionsEnabled?: boolean;
  clipRecommendationsEnabled?: boolean;
  // skip updating the redux store when we only want to use this component to fetch data, not updating the store
  skipStore?: boolean;
};

export function useChatHistory({
  historyLimit,
  recommendedActionsEnabled,
  clipRecommendationsEnabled,
  skipStore = false,
}: Props):
  | {
      loading: boolean;
      error: ApolloError | null;
      refreshHistory: (chatSessionId: string) => Promise<(ResponseMessage | UserMessage)[]>;
    }
  | undefined {
  const client = useApolloClient();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<ApolloError | null>(null);
  let isMounted = false; // Add this flag to prevent setting state on an unmounted component

  const { sessionState }: GlobalState = store.getState();
  const currentChatStatus = sessionState.chatState;

  const fetchData = async (chatSessionId: string) => {
    setLoading(true);
    try {
      const result = await client.query<ChatMessagesPaginatedResultQuery, ChatMessagesPaginatedResultQueryVariables>({
        query: aiCoachChatMessagesByChatSessionIdQuery,
        variables: {
          chatSessionId,
          limit: historyLimit,
          recommendedActionsEnabled,
          clipRecommendationsEnabled,
        },
        context: { clientName: 'gatewayGql' },
        fetchPolicy: 'network-only',
      });

      const newChatStatus = result.data.AiCoach?.queryByChatSessionId.session.status;
      if (newChatStatus && newChatStatus !== currentChatStatus && !skipStore) {
        setChatState(newChatStatus);
      }

      if (result.data?.AiCoach?.queryByChatSessionId?.elements) {
        const messages = result.data?.AiCoach?.queryByChatSessionId?.elements;
        const parsed_messages: UpdateChatHistory['messages'] = messages.map((message) => {
          if (message.senderType === MESSAGE_SENDER.CLIENT) {
            return {
              id: message.id,
              sender: message.senderType,
              text: message.message,
              timestamp: message.createdAt,
            };
          } else if (message.senderType === MESSAGE_SENDER.RESPONSE) {
            return {
              id: message.id,
              sender: message.senderType,
              text: message.message,
              timestamp: message.createdAt,
              /** A list of the clips that were recommended to the learner. It will be null in the case of a user message. */
              clipRecommendations: message.clipRecommendations,
              /** Feedback from the user on the AI coach response via a like/dislike */
              userFeedback: message.userFeedback,
              recommendedActions: message.recommendedActions,
              navigationButtons: message.navigationButtons,
              structuredResponse: message.structuredResponse,
            };
          }
          return {
            id: message.id,
            sender: message.senderType,
            text: message.message,
            timestamp: message.createdAt,
          };
        });

        const reversedMessages = parsed_messages.reverse();
        if (!skipStore) {
          // TODO: MAGIC: chat history is coming in reverse order, so we make it the new messages are at the bottom of the chat by reversing it here.
          updateChatHistory(reversedMessages);
        }
        setLoading(false);
        return reversedMessages;
      }
      return [];
    } catch (fetchError) {
      if (isMounted) setError(fetchError);
      if (isMounted) setLoading(false);

      const apolloError = fetchError as ApolloError;
      captureMessageInSentry({ error: apolloError, source: 'useChatHistory' });
      return [];
    }
  };

  React.useEffect(() => {
    // TODO: Assignments to the 'isMounted' variable from inside React Hook React.useEffect will be lost after each render. To preserve the value over time, store it in a useRef Hook and keep the mutable value in the '.current' property. Otherwise, you can move this variable directly inside React.useEffect.
    // FIXME: existing react-hooks/exhaustive-deps violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
    // eslint-disable-next-line react-hooks/exhaustive-deps
    isMounted = true;

    return () => {
      isMounted = false;
    };
  }, []); // Empty dependence array ensures this runs only once.

  return { loading, error, refreshHistory: fetchData };
}
